import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Body2,
  Button,
  Card,
  CheckboxCircleBlankIcon,
  ConfirmDialog,
  Header3,
  SkeletonLoader,
} from "@activecollab/components";
import { Divider } from "~/components/ModalObjects";
import { Page } from "~/components/Page";
import { PayPalFormDialog } from "~/pages/AppsAndIntegrations/Intergations/PayPalFormDialog";
import { useIntegrations } from "~/repository";
import { useUpdatePaymentGateway } from "~/repository/usePaymentGateways";
import Angie from "~/utils/angie";

const PayPalIntegrationPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { data, isFetched, isLoading } = useIntegrations();
  const { mutate: updatePaypal } = useUpdatePaymentGateway();
  const paypalIntegration = useMemo(() => {
    if (isFetched) {
      return data?.find((i) => i.short_name === "paypal-express");
    }
  }, [data, isFetched]);

  const onOpenDialog = useCallback(() => setOpenDialog(true), [setOpenDialog]);
  const onCloseDialog = useCallback(() => setOpenDialog(false), [setOpenDialog]);
  const openDisconnectDialog = useCallback(() => setIsDisconnectDialogOpen(true), []);
  const closeDisconnectDialog = useCallback(() => setIsDisconnectDialogOpen(false), []);

  const disablePaypal = useCallback(() => {
    updatePaypal(
      {
        paypal: {
          is_enabled: false,
        },
      },
      { onSettled: () => closeDisconnectDialog() }
    );
  }, [closeDisconnectDialog, updatePaypal]);

  return (
    <Page
      title="Add-On - Paypal"
      className="tw-max-w-[850px] tw-pb-4"
    >
      <Page.Header
        title={t("Paypal Express")}
        className="print:tw-hidden"
        backTitle={t("Apps & Integrations")}
        backUrl="/integrations"
      />
      <Card
        variant="paper-2"
        className="tw-px-8 tw-py-6 tw-h-full"
      >
        <div className="tw-flex tw-flex-1 sm:tw-gap-[60px] tw-gap-8 tw-mb-1 sm:tw-flex-row tw-flex-col-reverse">
          <div className="tw-flex tw-flex-1 tw-flex-col">
            <Header3>{t("Accept PayPal payments")}</Header3>
            <Body2 className="tw-mt-4">
              {t(
                "PayPal Express lets your clients pay invoices with their PayPal account. It makes payments quick and easy, since they don't have to enter their billing information."
              )}
            </Body2>
          </div>
          <div className="tw-w-[80px] tw-h-[80px] tw-flex tw-justify-end tw-items-end">
            <img
              src={`${Angie.assets_url}/system/images/integrations/paypal.png`}
              alt="PayPal Integration"
            />
          </div>
        </div>
        <Divider />
        {isLoading ? (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <SkeletonLoader data-testid="loader" />
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        ) : !paypalIntegration?.is_in_use ? (
          <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-gap-4 tw-flex-col">
              <Header3>{t("Configure PayPal Payments")}</Header3>
              <Body2>{t("There aren't any PayPal accounts configured yet.")}</Body2>
              <Button
                className="tw-w-fit"
                onClick={onOpenDialog}
              >
                {t("Configure Paypal")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-flex tw-gap-2 tw-items-center">
                <Header3 weight="bold">{t("PayPal Payments Enabled")}</Header3>
                <CheckboxCircleBlankIcon fill="var(--color-sucess-green)" />
              </div>
              <Body2>
                {t("Clients will see a PayPal button on their invoices and can pay directly through PayPal.")}
              </Body2>
            </div>
            <Divider />
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Header3>{t("Disable PayPal Payments")}</Header3>
              <Body2>
                {t("This will immediately disable PayPal payments. Any pending transactions will still process.")}
              </Body2>
            </div>
            <Button
              className="tw-w-fit tw-mt-4"
              onClick={openDisconnectDialog}
            >
              {t("Disconnect Paypal")}
            </Button>
          </>
        )}
        {openDialog ? (
          <PayPalFormDialog
            open={openDialog}
            onClose={onCloseDialog}
            integration={paypalIntegration}
          />
        ) : null}
        {isDisconnectDialogOpen ? (
          <ConfirmDialog
            open={isDisconnectDialogOpen}
            onConfirm={disablePaypal}
            onCancel={closeDisconnectDialog}
            dialogTitle={t("Disconnect from PayPal")}
            dialogContent={t("Your clients will no longer be able to pay your invoices using PayPal.")}
            confirmBtnText={t("Disconnect")}
            shouldShowCancelButton
          />
        ) : null}
      </Card>
    </Page>
  );
};

export default PayPalIntegrationPage;
